import type {
  Tool,
  ToolPatchPayload,
  ToolPostPayload,
} from "@repo/app-types/entities/tool";
import {
  getTagsInvalidatedByCreate,
  getTagsInvalidatedByDelete,
  getTagsInvalidatedByUpdate,
  getTagsProvidedByEntity,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";

const toolApi = api.injectEndpoints({
  endpoints: (builder) => ({
    allTools: builder.query<Tool[], void, { data: { tools: Tool[] } }>({
      query: () => "/tools",
      transformResponse: (response) => response.data.tools,
      providesTags: (result) => getTagsProvidedByList(TagType.Tool, result),
    }),
    voiceAgentSigningKey: builder.query<
      string,
      void,
      { data: { signing_key: string } }
    >({
      query: () => "/tools/voice-agent-signing-key",
      transformResponse: (response) => response.data.signing_key,
    }),
    getTool: builder.query<Tool, string, { data: { tool: Tool } }>({
      query: (toolId) => `/tools/${toolId}`,
      transformResponse: (response) => response.data.tool,
      providesTags: (result, error, toolId) =>
        getTagsProvidedByEntity(TagType.Tool, toolId),
    }),
    createTool: builder.mutation<
      Tool,
      ToolPostPayload,
      { data: { tool: Tool } }
    >({
      query: (payload) => ({
        url: "/tools",
        method: "POST",
        body: payload,
      }),
      transformResponse: (response) => response.data.tool,
      invalidatesTags: getTagsInvalidatedByCreate(TagType.Tool),
    }),
    updateTool: builder.mutation<
      Tool,
      { toolId: string; payload: ToolPatchPayload },
      { data: { tool: Tool } }
    >({
      query: ({ toolId, payload }) => ({
        url: `/tools/${toolId}`,
        method: "PATCH",
        body: payload,
      }),
      transformResponse: (response) => response.data.tool,
      invalidatesTags: (result, error, { toolId }) =>
        getTagsInvalidatedByUpdate(TagType.Tool, toolId),
    }),
    deleteTool: builder.mutation<void, string>({
      query: (toolId) => ({
        url: `/tools/${toolId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, toolId) =>
        getTagsInvalidatedByDelete(TagType.Tool, toolId),
    }),
  }),
});

export const {
  useAllToolsQuery,
  useGetToolQuery,
  useCreateToolMutation,
  useUpdateToolMutation,
  useDeleteToolMutation,
  useVoiceAgentSigningKeyQuery,
} = toolApi;
